import './HWILComponents.scss'
import {
    StepperDropdownContainer,
    StepperDropdownContent,
    StepperDropdownHeader,
    StepperDropdownHeaderIcon,
    StepperDropdownHeaderTitle
} from '../../dropdown/StepperDropdown'
import { CloseSvg, HWILConfigSvg, InfoSvg, StepCompletedSvg } from '../../../../../CommonComponents/svgs/Svgs'
import { FC, useMemo } from 'react'
import { StepperProps } from '../../../utils/stepper-types'
import useForm from '../../../hooks/useForm'
import useStaticData from '../../../hooks/useStaticData'
import useModal from '../../../hooks/useModal'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import { isIPValid } from '../../../../../../utils/commonFunctions'
import { getInitialFormData } from '../../../utils/constants'
import { RemoteAppDataType, HwilComponentType } from '../../../../types'

const HWILComponents: FC<StepperProps> = ({ title, hideComponent, open, disabled, stepCompleted, onToggle }) => {

    const { setModals } = useModal()
    const { formData, setFormData } = useForm()
    const { allPayloads } = useStaticData()

    const selectedComponents = useMemo(() => formData?.hwil?.hwInfo, [formData?.hwil?.hwInfo])
    const payloads = useMemo(() => selectedComponents.filter(comp => comp?.category === 'Payload'), [selectedComponents])
    const remotePayloadAppData: RemoteAppDataType[] = useMemo(() => formData?.hwil?.remoteAppConfiguration?.remoteApp?.AppData || [], [formData?.hwil?.remoteAppConfiguration?.remoteApp?.AppData])
    const bus = useMemo(() => selectedComponents.filter(comp => comp?.category !== 'Payload'), [selectedComponents])
    const showSelectButton = useMemo(() => payloads?.length === 0 || bus?.length === 0, [payloads?.length, bus?.length])
    const selectedSatellite = useMemo(() => formData?.satellites?.[0], [formData?.satellites])
    const groupPayloads = useMemo(() => allPayloads[selectedSatellite?.groupId], [allPayloads, selectedSatellite])

    const showConfigInfo = useMemo(() => {
        if (!remotePayloadAppData || remotePayloadAppData.length === 0) return false;
        return remotePayloadAppData.some(payload => {
            const Interfaces = payload?.interfaces || [];
            const hasNoInterfaces = Interfaces.length === 0;
            const invalidCAN = Interfaces.includes('CAN') && payload?.canPath0 === '';
            const invalidEthernet = Interfaces.includes('Ethernet') && !isIPValid(payload?.payloadDeviceIP ?? '');
            return hasNoInterfaces || invalidCAN || invalidEthernet;
        });
    }, [remotePayloadAppData]);

    const openModal = () => {
        setModals((prev) => ({
            ...prev,
            configured: {
                name: 'hwilComponent',
                open: true,
                data: {
                    payload: payloads || [],
                    bus: bus || [],
                    groupPayloads
                }
            }
        }))
    }
    const openConfigModal = (component: RemoteAppDataType | undefined) => {
        if (!component) return
        setModals((prev) => ({
            ...prev,
            configured: {
                name: 'payloadAppConfig',
                open: true,
                data: component
            }
        }))
    }
    const removeComponent = (componentType: 'payload' | 'bus', component: HwilComponentType) => {
        const INITIAL_FORM_DATA = getInitialFormData()
        setFormData(prev => ({
            ...prev,
            hwil: {
                hwInfo: prev.hwil.hwInfo.filter(comp => comp?.uniqueId !== component?.uniqueId),
                remoteAppConfiguration: componentType === 'payload' ? INITIAL_FORM_DATA.hwil.remoteAppConfiguration : prev.hwil.remoteAppConfiguration
            }
        }));
    }

    if (hideComponent) return null
    return (
        <StepperDropdownContainer
            open={open}
            disabled={disabled}
        >
            <StepperDropdownHeader
                title={title}
                onToggle={onToggle}
            >
                <StepperDropdownHeaderTitle>
                    {title}
                </StepperDropdownHeaderTitle>
                <StepperDropdownHeaderIcon show={stepCompleted}>
                    <StepCompletedSvg />
                </StepperDropdownHeaderIcon>
            </StepperDropdownHeader>
            <StepperDropdownContent>
                {showConfigInfo &&
                    <div className='information__container'>
                        <InfoSvg />
                        <span className='information__text'>Please configure the selected payload interfaces to continue.</span>
                    </div>
                }
                {payloads?.length > 0 &&
                    <div className='hwil__compnents__section'>
                        <span className='component__section__header__text'>Payload</span>
                        <div className='compnents__section__body'>
                            {payloads.map((payload) => {
                                const configurableInterfaces = ['GPIO', 'CAN', 'UART', 'Ethernet']
                                const matchedPayload = remotePayloadAppData.find(payloadData => payloadData?.id === payload?.uniqueId)
                                const payloadInterfaces = matchedPayload?.interfaces || []
                                const hasConfigOption = payloadInterfaces.some(interface_ => configurableInterfaces.includes(interface_))

                                return (
                                    <div className='compnents__section__list__item__container' key={payload?.uniqueId}>
                                        <div className='list__item__text__container'>
                                            <CustomToolTip title={payload?.name} placement='top-start'>
                                                {payload?.name}
                                            </CustomToolTip>
                                        </div>
                                        <div className='list__item__button__container'>
                                            {hasConfigOption &&
                                                <CustomToolTip title='Configure' placement='top'>
                                                    <button className='hwil__config__button' onClick={() => openConfigModal(matchedPayload)}>
                                                        <HWILConfigSvg />
                                                    </button>
                                                </CustomToolTip>
                                            }
                                            <CustomToolTip title='Remove' placement='top'>
                                                <button className='action__button'
                                                    onClick={() => removeComponent('payload', payload)}
                                                >
                                                    <CloseSvg />
                                                </button>
                                            </CustomToolTip>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                }
                {bus?.length > 0 &&
                    <div className='hwil__compnents__section'>
                        <span className='component__section__header__text'>Bus Components</span>
                        <div className='compnents__section__body'>
                            {bus.map(buscom => {
                                return (
                                    <div className='compnents__section__list__item__container' key={buscom?.uniqueId}>
                                        <div className='list__item__text__container'>
                                            <CustomToolTip title={buscom?.name} placement='top-start'>
                                                {buscom?.name}
                                            </CustomToolTip>
                                        </div>
                                        <div className='list__item__button__container'>
                                            <CustomToolTip title='Remove' placement='top'>
                                                <button className='action__button'
                                                    onClick={() => removeComponent('bus', buscom)}
                                                >
                                                    <CloseSvg />
                                                </button>
                                            </CustomToolTip>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                }
                {showSelectButton &&
                    <button className={`hwil__modal__open__button`} onClick={openModal}>
                        Select Component
                    </button>
                }
            </StepperDropdownContent>
        </StepperDropdownContainer>
    )
}

export default HWILComponents